/**
 * Read markers, client side (#1575 story 3.2).
 *
 * One entry point for "the person opened this request": send it now, or queue it
 * for replay, and in either case leave the screen alone. Two callers —
 * `useRecordRequestRead` on `RequestDetail` mount, and
 * `syncService.syncOfflineReadOperations` when the queue drains — so the rules
 * about what counts as offline, what is worth keeping, and what the list should
 * look like afterwards are stated once.
 *
 * ⚠️ NOTHING HERE EVER THROWS OR RESOLVES WITH A FAILURE THE UI MUST HANDLE.
 * A read marker is a convenience, not content the person authored. The screen
 * must never block, spin or show an error for it (AC 4), so `recordRequestRead`
 * resolves with an outcome for tests and logging and swallows everything else.
 * Callers still attach a `.catch` (`useRecordRequestRead`) rather than trusting
 * that: an unhandled rejection from a call nobody is listening to would be a red
 * box on iOS.
 *
 * ⚠️ OFFLINE IS `networkService.isConnected()` AND A STATUS-0 `ApiError`, NOT A
 * 5xx. A masked server 500 reads exactly like "saved offline" if you let it, and
 * this project has already been bitten by that. A 500 is dropped: the next open
 * of the same request tries again, which is cheaper and more honest than a queue
 * entry that will keep failing for the same reason.
 *
 * ⚠️ A 4xx IS NEVER QUEUED AND NEVER RETRIED (AC 4). `INVALID_READ_AT` is the
 * one 4xx with a second chance, and only on the direct path — see
 * `recordRequestRead`.
 *
 * @see server/routes/requests.route.ts — `POST /:id/read`
 * @see server/services/resource-read.service.ts — `validateReadAt`
 */

import { ApiError } from './api';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { RequestsService } from './requestsService';
import { offlineRequestsKeys } from '../hooks/queries/useOfflineRequestsQuery';
import { requestsKeys } from '../hooks/queries/useRequestsQuery';
import { queryClient as sharedQueryClient } from '../lib/queryClient';

import type { QueryClient } from '@tanstack/react-query';

/**
 * The server's stable code for "that `readAt` is malformed or in the future"
 * (`ReadAtValidationError`). Matched on the code, never on the bare 400: an
 * intermediary can produce a 400 of its own.
 */
export const INVALID_READ_AT_CODE = 'INVALID_READ_AT';

/** What became of one attempt. Returned for tests and `__DEV__` logging only. */
export type ReadMarkerOutcome =
  /** The server has it. */
  | 'sent'
  /** Offline or a network failure: queued for replay. */
  | 'queued'
  /** The server refused it permanently (a 4xx). Dropped, never retried. */
  | 'rejected'
  /** Anything else (a 5xx, an unexpected throw). Dropped; the next open retries. */
  | 'dropped';

/** An `ApiError` the transport never got an answer for (`api.ts` uses status 0). */
const isNetworkFailure = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 0;

/** A permanent refusal: the server answered, and it answered 4xx. */
const isPermanentRejection = (error: unknown): boolean =>
  error instanceof ApiError && error.status >= 400 && error.status < 500;

const devWarn = (
  outcome: ReadMarkerOutcome,
  requestId: string,
  error?: unknown,
) => {
  if (!__DEV__) return;
  // ids and a status only — never a body, never an address (NFR54).
  console.warn({
    operation: 'request.readMarker',
    requestId,
    outcome,
    status: error instanceof ApiError ? error.status : undefined,
    code: error instanceof ApiError ? error.code : undefined,
  });
};

/**
 * Walk one React Query cache entry and clear `hasUnseenChanges` on the row for
 * `requestId`, whatever shape that entry happens to hold.
 *
 * Three shapes reach here from the two key prefixes below, and a fourth will the
 * moment somebody adds a cache: a paginated page (`{ data: Request[] }`), a plain
 * list (`Request[]`), and a single request. A shape it cannot read contributes
 * nothing rather than being rewritten into something the screen cannot render.
 *
 * Returns the IDENTICAL reference when nothing matched, so an untouched page does
 * not re-render every row on every open.
 */
const clearRowInCacheEntry = (data: unknown, requestId: string): unknown => {
  if (Array.isArray(data)) {
    let changed = false;
    const next = data.map(row => {
      const cleared = clearRowInCacheEntry(row, requestId);
      if (cleared !== row) changed = true;
      return cleared;
    });
    return changed ? next : data;
  }

  if (typeof data !== 'object' || data === null) {
    return data;
  }

  const record = data as {
    id?: unknown;
    data?: unknown;
    hasUnseenChanges?: unknown;
  };

  if (Array.isArray(record.data)) {
    const nextRows = clearRowInCacheEntry(record.data, requestId);
    return nextRows === record.data ? data : { ...record, data: nextRows };
  }

  if (String(record.id) !== requestId) {
    return data;
  }
  // `false` rather than deleted: story 3.1's renderers read `=== true`, so both
  // read as seen, and `false` says "decided: read" where absent says "unknown".
  return record.hasUnseenChanges === false
    ? data
    : { ...record, hasUnseenChanges: false };
};

/**
 * Clear the unread flag for `requestId` on every cached list the request list
 * screen can draw from (AC 5), so the row stops being bold the moment the read
 * is TAKEN rather than at the next fetch.
 *
 * ⚠️ ON A QUEUED READ TOO, NOT ONLY A SENT ONE (owner's call, 2026-10-01). AC 5
 * as written said "after a successful read (direct or replayed)", and a queued
 * read is neither, so offline the person opened a request, came back to the list
 * and found it still shouting unread until the device reconnected. In an app
 * whose first rule is that it works offline, their own open is the strongest
 * signal there is, and this write is per-viewer and cosmetic.
 *
 * ⚠️ THE COST, STATED: if that queued entry is later DROPPED — the replay drops
 * any permanent 4xx, `INVALID_READ_AT` from a fast clock being the realistic one
 * — then the next list fetch returns `hasUnseenChanges: true` and the row goes
 * bold again. That is not a glitch to paper over: the read genuinely never
 * reached the server, and a row that re-bolds is the honest report of it. The
 * alternative (never clearing until it lands) was the old behaviour, and it cost
 * every offline reader a list that disagreed with what they had just done.
 *
 * ⚠️ A LOCAL WRITE, NOT AN INVALIDATION. `#1069`'s thread-read invalidates these
 * same two prefixes, which is right for a flag the server recomputes on demand.
 * It would be wrong here, twice over: offline there is no fetch to invalidate
 * into, and on a REPLAYED read the server's own `hasUnseenChanges` answer was
 * computed before the marker moved, so a refetch would hand the bold row straight
 * back. `ViewRequests` renders from `requestsKeys.paginated(...)` with
 * `offlineRequestsKeys.lists()` as its fallback (story 2.2, C6), so both are
 * written — or the fallback row could contradict the read the person just made.
 *
 * The flag is per-viewer, so the PERSISTED cache is deliberately not touched:
 * `offlineCache.stripViewerScopedFields` already drops `hasUnseenChanges` on
 * every read out of it, and a row that never carries the flag cannot be bold.
 *
 * `client` is injectable for tests; production uses the app-wide singleton
 * (`App.tsx` provides this same instance), the established pattern for a cache
 * write from a non-React caller — `syncService` has no provider to read from.
 */
export const clearCachedUnseenChanges = (
  requestId: string,
  client: QueryClient = sharedQueryClient,
): void => {
  try {
    client.setQueriesData({ queryKey: requestsKeys.all }, (data: unknown) =>
      clearRowInCacheEntry(data, requestId),
    );
    client.setQueriesData(
      { queryKey: offlineRequestsKeys.lists() },
      (data: unknown) => clearRowInCacheEntry(data, requestId),
    );
  } catch (error) {
    // A cache write is cosmetic; it must not turn a recorded read into a failure.
    devWarn('dropped', requestId, error);
  }
};

/**
 * Record that the person opened `requestId` at `readAt`. Fire and forget.
 *
 * Offline (or a network failure mid-flight) queues the marker with the ORIGINAL
 * `readAt` so a later replay records when they actually looked, not when the
 * device reconnected.
 *
 * ⚠️ ONE BOUNDED SECOND ATTEMPT, FOR `INVALID_READ_AT` ONLY. The endpoint refuses
 * a future `readAt` with no skew allowance at all (story 1.2 AC 1), so on a device
 * whose clock runs even a second ahead EVERY open would be refused and no row
 * would ever stop being bold. Story 1.2's own hand-off names the fix: such a
 * client should omit `readAt` and take the server's `now`. That is what this does
 * — once, on the direct path only, where `now` is within the round trip of the
 * real open time. A queued replay gets no such fallback: `now` there is the replay
 * time, which would mark every change since the open as seen, so the replay drops
 * the entry instead (`syncOfflineReadOperations`).
 */
export const recordRequestRead = async (
  requestId: string,
  readAt: string,
): Promise<ReadMarkerOutcome> => {
  try {
    if (!networkService.isConnected()) {
      await offlineCache.addReadToOfflineQueue(requestId, readAt);
      clearCachedUnseenChanges(requestId);
      return 'queued';
    }

    try {
      await RequestsService.markRead(requestId, readAt);
    } catch (error) {
      if (error instanceof ApiError && error.code === INVALID_READ_AT_CODE) {
        devWarn('rejected', requestId, error);
        await RequestsService.markRead(requestId);
      } else {
        throw error;
      }
    }

    clearCachedUnseenChanges(requestId);
    return 'sent';
  } catch (error) {
    try {
      if (isNetworkFailure(error)) {
        await offlineCache.addReadToOfflineQueue(requestId, readAt);
        clearCachedUnseenChanges(requestId);
        return 'queued';
      }
      const outcome: ReadMarkerOutcome = isPermanentRejection(error)
        ? 'rejected'
        : 'dropped';
      devWarn(outcome, requestId, error);
      return outcome;
    } catch (queueError) {
      devWarn('dropped', requestId, queueError);
      return 'dropped';
    }
  }
};
